const { registerBlockType } = wp.blocks;
const { RichText, InspectorControls, MediaUpload, MediaUploadCheck } =
  wp.blockEditor;
const { Button, Icon, TextControl, PanelBody, SelectControl } = wp.components;
const { Fragment, useEffect, useState } = wp.element;
const { __ } = wp.i18n;

import { parseHubSpotShortcode } from '../utils/methods';

registerBlockType('pg/demo-form', {
  title: 'PG Demo Form',
  icon: 'welcome-learn-more',
  category: 'layout',
  attributes: {
    sectionTitle: { type: 'string', default: '' },
    description: { type: 'string', default: '' },
    checklist: { type: 'array', default: ['Example item...'] },
    images: { type: 'array', default: [] },
    topImages: { type: 'array', default: [] }, // New attribute for top images
    formShortcode: { type: 'string', default: '' },
    sectionClass: { type: 'string', default: 'section-light-primary' },
    containerClass: { type: 'string', default: 'dark' },
    layout: { type: 'string', default: 'centered' },
    formTitle: { type: 'string', default: '' },
  },
  edit: ({ attributes, setAttributes }) => {
    const {
      sectionTitle,
      description,
      checklist,
      images,
      topImages,
      formShortcode,
      sectionClass,
      containerClass,
      layout,
      formTitle,
    } = attributes;

    const [loaded, setLoaded] = useState(false);

    const convertedImages = images.map((image) => {
      if (image instanceof String) {
        return { url: image, alt: '' };
      }
      return image;
    });

    const convertedTopImages = topImages.map((image) => {
      if (image instanceof String) {
        return { url: image, alt: '' };
      }
      return image;
    });

    const loadHubSpotScript = () => {
      if (window.hbspt) {
        setLoaded(true);
        return;
      }

      const script = document.createElement('script');
      script.src = '//js.hsforms.net/forms/v2.js';
      script.onload = () => setLoaded(true);
      document.body.appendChild(script);
    };

    useEffect(() => {
      loadHubSpotScript();
    }, []);

    useEffect(() => {
      if (loaded && formShortcode) {
        const { portalId, formId } = parseHubSpotShortcode(formShortcode);
        if (portalId && formId) {
          const formClassTarget = `.my-hubspot-form-${formId}`;
          window.hbspt.forms.create({
            portalId: portalId,
            formId: formId,
            target: formClassTarget,
          });
        }
      }
    }, [loaded, formShortcode]);

    const updateChecklist = (index, value) => {
      const newChecklist = [...checklist];
      newChecklist[index] = value;
      setAttributes({ checklist: newChecklist });
    };

    const addChecklistItem = () => {
      setAttributes({ checklist: [...checklist, ''] });
    };

    const removeChecklistItem = (index) => {
      const newChecklist = checklist.filter((_, i) => i !== index);
      setAttributes({ checklist: newChecklist });
    };

    const addImage = (media) => {
      setAttributes({
        images: [
          ...images,
          {
            url: media.sizes?.['medium_large']?.url || media.url,
            alt: media.alt || '',
          },
        ],
      });
    };

    const addTopImage = (media) => {
      setAttributes({
        topImages: [
          ...topImages,
          {
            url: media.sizes?.['medium_large']?.url || media.url,
            alt: media.alt || '',
          },
        ],
      });
    };

    const updateImage = (index, media) => {
      const newImages = [...images];
      newImages[index] = {
        url:
          media.sizes?.['large']?.url || media.sizes?.medium?.url || media.url,
        alt: media.alt || '',
      };
      setAttributes({ images: newImages });
    };

    const updateTopImage = (index, media) => {
      const newTopImages = [...topImages];
      newTopImages[index] = {
        url:
          media.sizes?.['large']?.url || media.sizes?.medium?.url || media.url,
        alt: media.alt || '',
      };
      setAttributes({ topImages: newTopImages });
    };

    const removeImage = (index) => {
      const newImages = images.filter((_, i) => i !== index);
      setAttributes({ images: newImages });
    };

    const removeTopImage = (index) => {
      const newTopImages = topImages.filter((_, i) => i !== index);
      setAttributes({ topImages: newTopImages });
    };

    const onChangeSectionClass = (newClass) => {
      setAttributes({ sectionClass: newClass });
    };

    const onChangeContainerClass = (newClass) => {
      setAttributes({ containerClass: newClass });
    };

    const onChangeLayout = (newLayout) => {
      setAttributes({ layout: newLayout });
    };

    const getWrapperClassName = () => {
      const classes = ['get-demo-section-wrapper'];
      if (containerClass === 'light') classes.push('light');
      classes.push(
        layout === 'wide' ? 'custom-form-wide' : 'custom-form-centered'
      );
      return classes.join(' ');
    };

    return (
      <Fragment>
        <InspectorControls>
          <PanelBody title={__('Form Settings')}>
            <TextControl
              label={__('HubSpot Form Shortcode')}
              value={formShortcode}
              onChange={(value) => setAttributes({ formShortcode: value })}
            />
            <SelectControl
              label={__('Section Class')}
              value={sectionClass}
              options={[
                { label: 'Dark Secondary', value: 'section-dark-secondary' },
                { label: 'Dark Primary', value: 'section-dark-primary' },
                { label: 'Light Primary', value: 'section-light-primary' },
                { label: 'Light Secondary', value: 'section-light-secondary' },
              ]}
              onChange={onChangeSectionClass}
            />
            <SelectControl
              label={__('Wrapper Class')}
              value={containerClass}
              options={[
                { label: 'Dark (default)', value: 'dark' },
                { label: 'Light', value: 'light' },
              ]}
              onChange={onChangeContainerClass}
            />
            <SelectControl
              label={__('Layout')}
              value={layout}
              options={[
                { label: 'Centered (default)', value: 'centered' },
                { label: 'Wide', value: 'wide' },
              ]}
              onChange={onChangeLayout}
            />
          </PanelBody>
        </InspectorControls>
        <section className={sectionClass}>
          <div className={getWrapperClassName()}>
            <div className="content-container">
              <div className="content-wrapper">
                {/* Top Images */}
                <div className="top-images">
                  {convertedTopImages.map((image, index) => (
                    <div key={index}>
                      <img
                        src={image.url}
                        alt={image.alt ?? `Top Image ${index + 1}`}
                      />
                      <Button
                        isSmall
                        isPrimary
                        className="removeStaticButton"
                        onClick={() => removeTopImage(index)}
                      >
                        <Icon icon="trash" /> {__('Remove Top Image')}
                      </Button>
                    </div>
                  ))}
                  <MediaUploadCheck>
                    <MediaUpload
                      onSelect={(media) => addTopImage(media)}
                      allowedTypes={['image']}
                      render={({ open }) => (
                        <Button
                          onClick={open}
                          isPrimary
                          className={'addButton'}
                        >
                          <Icon icon="format-image" /> {__('Add Top Image')}
                        </Button>
                      )}
                    />
                  </MediaUploadCheck>
                </div>

                <RichText
                  tagName="h2"
                  value={sectionTitle}
                  onChange={(value) => setAttributes({ sectionTitle: value })}
                  placeholder={__('Title...')}
                />
                <div className={'description'}>
                  <RichText
                    tagName={'p'}
                    value={description}
                    onChange={(newDescription) =>
                      setAttributes({ description: newDescription })
                    }
                    placeholder={__('Description')}
                  />
                </div>
                <div className="checklist">
                  {checklist.map((item, index) => (
                    <div key={index} className="check-item">
                      <RichText
                        tagName="p"
                        value={item}
                        onChange={(value) => updateChecklist(index, value)}
                        placeholder={__('Enter checklist item...')}
                      />
                      <Button
                        isDestructive
                        onClick={() => removeChecklistItem(index)}
                      >
                        {__('Remove')}
                      </Button>
                    </div>
                  ))}
                  <Button isPrimary onClick={addChecklistItem}>
                    {__('Add Checklist Item')}
                  </Button>
                </div>
                <div className="images">
                  {convertedImages.map((image, index) => (
                    <div key={index}>
                      <img
                        src={image.url}
                        alt={image.alt ?? `Image ${index + 1}`}
                      />
                      <Button
                        isSmall
                        isPrimary
                        className="removeStaticButton"
                        onClick={() => removeImage(index)}
                      >
                        <Icon icon="trash" /> {__('Remove Image')}
                      </Button>
                    </div>
                  ))}
                  <MediaUploadCheck>
                    <MediaUpload
                      onSelect={(media) => addImage(media)}
                      allowedTypes={['image']}
                      render={({ open }) => (
                        <Button
                          onClick={open}
                          isPrimary
                          className={'addButton'}
                        >
                          <Icon icon="format-image" /> {__('Add Image')}
                        </Button>
                      )}
                    />
                  </MediaUploadCheck>
                </div>
              </div>
              <div className="form-wrapper">
                <RichText
                  tagName="h3"
                  value={formTitle}
                  onChange={(value) => setAttributes({ formTitle: value })}
                  placeholder={__('Enter Form Title...')}
                />
                {parseHubSpotShortcode(formShortcode) && (
                  <div
                    className={`my-hubspot-form-${parseHubSpotShortcode(formShortcode).formId || ''}`}
                  ></div>
                )}
              </div>
            </div>
          </div>
        </section>
      </Fragment>
    );
  },
  save: ({ attributes }) => {
    const {
      sectionTitle,
      description,
      checklist,
      images,
      topImages,
      formShortcode,
      sectionClass,
      containerClass,
      layout,
      formTitle,
    } = attributes;

    const convertedImages = images.map((image) => {
      if (image instanceof String) {
        return { url: image, alt: '' };
      }
      return image;
    });

    const convertedTopImages = topImages.map((image) => {
      if (image instanceof String) {
        return { url: image, alt: '' };
      }
      return image;
    });

    const getWrapperClassName = () => {
      const classes = ['get-demo-section-wrapper'];
      if (containerClass === 'light') classes.push('light');
      classes.push(
        layout === 'wide' ? 'custom-form-wide' : 'custom-form-centered'
      );
      return classes.join(' ');
    };

    <div className="images">
      {images.map((image, index) => (
        <img
          key={index}
          src={image.url}
          alt={image.alt || `Image ${index + 1}`}
        />
      ))}
    </div>;

    return (
      <section className={sectionClass}>
        <div className={getWrapperClassName()}>
          <div className="content-container">
            <div className="content-wrapper">
              <div className="top-images">
                {convertedTopImages.map((image, index) => (
                  <img
                    key={index}
                    src={image.url}
                    alt={image.alt || `Top Image ${index + 1}`}
                  />
                ))}
              </div>
              <RichText.Content tagName="h2" value={sectionTitle} />
              <div className={'description'}>
                <RichText.Content tagName="p" value={description} />
              </div>
              <div className="checklist">
                {checklist.map((item, index) => (
                  <RichText.Content
                    key={index}
                    tagName="p"
                    className="check-item"
                    value={item}
                  />
                ))}
              </div>
              <div className="images">
                {convertedImages.map((image, index) => (
                  <img
                    key={index}
                    src={image.url}
                    alt={image.alt || `Image ${index + 1}`}
                  />
                ))}
              </div>
            </div>
            <div className="form-wrapper">
              {/* Form Title */}
              <RichText.Content tagName="h3" value={formTitle} />
              {parseHubSpotShortcode(formShortcode) && (
                <div
                  className={`my-hubspot-form-${parseHubSpotShortcode(formShortcode).formId || ''}`}
                ></div>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  },
});
